import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/lib/i18n";

/*
  VORFÜHRUNG — temporärer Aufbau an einem Outlet-Center in Brandenburg.
  Kennzeichnung: VORFÜHRUNG · FOTO (temporärer Aufbau) — bewusst getrennt
  von HEUTE · FOTO (gebaute Anlage) und VISUALISIERUNG (Rendering).
  Keine Kunden-, Marken- oder Ortsnamen außer Brandenburg.
*/

const M = "/media/vorfuehrung";
const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "", testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1, delay, ease }}
    className={className}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const DemoLabel = () => {
  const { lang } = useLang();
  return (
    <div className="absolute top-3 left-3 md:top-4 md:left-4 z-10 inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-white/85 bg-black/50 backdrop-blur px-3 py-1.5 border border-white/10 font-display">
      <span className="h-1 w-1 rounded-full bg-white/70" />
      {lang === "de" ? "Vorführung · Foto" : "Demonstration · Photo"}
    </div>
  );
};

const Photo = ({ src, alt, caption, aspect, testid }) => (
  <figure data-testid={testid} className="w-full">
    <div className={`relative w-full ${aspect} overflow-hidden border border-white/10`}>
      <img
        src={src}
        alt={alt || caption}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <DemoLabel />
    </div>
    <p className="mt-3 text-[13px] leading-relaxed text-white/50 max-w-3xl">{caption}</p>
  </figure>
);

export default function Vorfuehrung() {
  const { lang } = useLang();
  const copy = {
    de: {
      title: "Das Modul ist eine Plattform.",
      intro:
        "Aufgebaut zur Vorführung für Entscheidungsträger aus Politik und Verwaltung, am Outlet-Center in Wustermark, Brandenburg.",
      capFrontal:
        "Drei Module als durchgehende Linie. Dazwischen befahrbare Rampen und taktile Bodenplatten — der Weg bleibt für alle offen.",
      capEbike:
        "Ein Modul trägt eine E-Bike-Ladestation mit zwei Ladepunkten — und oben einen Baum. Technik und Grün im selben Bauteil, nicht nebeneinander.",
      capReihe:
        "Eine Modulseite als Werbefläche. Was die Stadt begrünt und schützt, kann sich mittragen.",
      altFrontal: "Drei Berlin-Cube-Module als durchgehende Linie bei der Vorführung in Wustermark, Brandenburg",
      altEbike: "Berlin-Cube-Modul mit E-Bike-Ladestation und Baum bei der Vorführung in Wustermark, Brandenburg",
      altReihe: "Berlin-Cube-Modul mit Werbefläche bei der Vorführung in Wustermark, Brandenburg",
    },
    en: {
      title: "The module is a platform.",
      intro:
        "Set up as a demonstration for decision-makers from politics and public administration, at an outlet centre in Wustermark, Brandenburg.",
      capFrontal:
        "Three modules in a continuous line. Drivable ramps and tactile paving in between — the route stays open for everyone.",
      capEbike:
        "One module carries an e-bike charging station with two charging points — and a tree on top. Technology and greenery in the same unit, not side by side.",
      capReihe:
        "One module face as an advertising surface. What greens and protects a city can help pay for itself.",
      altFrontal: "Three Berlin-Cube modules in a continuous line at the demonstration in Wustermark, Brandenburg",
      altEbike: "Berlin-Cube module with e-bike charging station and tree at the demonstration in Wustermark, Brandenburg",
      altReihe: "Berlin-Cube module with advertising surface at the demonstration in Wustermark, Brandenburg",
    },
  }[lang];

  return (
    <section
      id="vorfuehrung"
      data-testid="section-vorfuehrung"
      className="relative bg-[#0F1114] text-white pb-32 md:pb-48 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        <div className="mb-16 md:mb-24 max-w-4xl border-t border-white/10 pt-24 md:pt-32">
          <Reveal>
            <h2
              className="font-display font-light tracking-[-0.03em] leading-[0.98] text-white"
              style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)" }}
              data-testid="vorfuehrung-title"
            >
              {copy.title}
            </h2>
            <p
              className="mt-8 md:mt-10 text-white/70 font-display font-light tracking-[-0.005em] leading-[1.4] max-w-3xl"
              style={{ fontSize: "clamp(1.05rem, 1.5vw, 1.45rem)" }}
              data-testid="vorfuehrung-intro"
            >
              {copy.intro}
            </p>
          </Reveal>
        </div>

        <div className="space-y-14 md:space-y-20">
          <Reveal testid="vorfuehrung-img-frontal">
            <Photo
              src={`${M}/w_frontal.jpg`}
              alt={copy.altFrontal}
              caption={copy.capFrontal}
              aspect="aspect-[4/3] md:aspect-[16/9]"
            />
          </Reveal>

          <div className="grid md:grid-cols-2 gap-8 md:gap-5">
            <Reveal testid="vorfuehrung-img-ebike">
              <Photo
                src={`${M}/w_ebike_angle.jpg`}
                alt={copy.altEbike}
                caption={copy.capEbike}
                aspect="aspect-[4/3]"
              />
            </Reveal>
            <Reveal delay={0.08} testid="vorfuehrung-img-reihe">
              <Photo
                src={`${M}/w_reihe.jpg`}
                alt={copy.altReihe}
                caption={copy.capReihe}
                aspect="aspect-[4/3]"
              />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
